import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { FC, ReactElement, ReactNode, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { preparePageLoadAnalytics } from '../lib/utils/analytics';
import AlertProvider from '../components/alert/alertProvider';
import { getCurrentEnv } from '../lib/utils/utils';
import '../styles/global.scss';
import { Provider } from '../context';
import '@pulse-community/typography-css/dist/main.css';
import '@digital-pulse/react/style.css';
import '@digital-pulse/design-tokens/blue.css';
import { FeatureFlagWrapper } from '../components/featureFlagWrapper';
import { I18nContextObj, I18nDefaultProvider } from '@cvsdigital_web/react-cmk-i18n';
import { getTranslateApiKey, getTranslateApiUrl } from '../lib/config/translationConfig';

// xhr2 is a Node.js-only polyfill — only assign server-side
if (typeof window === 'undefined') {
  global.XMLHttpRequest = require('xhr2');
}

// Devtools bundle excluded from production entirely
const ReactQueryDevtools =
  process.env.NODE_ENV !== 'production'
    ? dynamic(() =>
        import('@tanstack/react-query-devtools').then(m => ({ default: m.ReactQueryDevtools }))
      )
    : () => null;

type NextPageWithLayout = NextPage & {
  getLayout?: (pageProps: AppProps, page: ReactElement) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false
    }
  }
});

const currentEnv = getCurrentEnv();

function MyApp({ Component, pageProps }: AppPropsWithLayout | any) {
  if (typeof window !== 'undefined') {
    preparePageLoadAnalytics();
  }

  // Lazy-load web component registrations after hydration so they don't block initial parse
  useEffect(() => {
    import('../register-web-components');
  }, []);

  const env = currentEnv === '' || currentEnv === 'PROD' || currentEnv === 'PROD-ES' || currentEnv === 'CSR' ? 'prod' : 'qa';

  const i18nContext = new I18nContextObj(
    undefined,
    getTranslateApiKey() as any,
    getTranslateApiUrl() as any,
    undefined,
    true
  );

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1"></meta>
        {typeof window !== 'undefined' && (
          <>
            <script
              type={'text/javascript'}
              src={`https://tags.tiqcdn.com/utag/cvs/cvs/${env}/utag.sync.js`}
            />
            <script
              id={`id-utag`}
              type={'text/javascript'}
              async={true}
              src={`https://tags.tiqcdn.com/utag/cvs/cvs/${env}/utag.js`}
            />
          </>
        )}
      </Head>
      <>
        <QueryClientProvider client={queryClient}>
          <I18nDefaultProvider value={i18nContext}>
            <Provider>
              <AlertProvider>
                <Component {...pageProps} />
              </AlertProvider>
            </Provider>
          </I18nDefaultProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </>
    </>
  );
}

export default FeatureFlagWrapper(MyApp as FC);
